import { useState, useEffect, useRef } from "react";
import { Loader2, Lock, Upload, Check, MessageCircle, Mail, RefreshCw, ArrowRight, CheckCircle, Trash2, AlertTriangle, Coins, Star, ArrowDownCircle, Gift, Shield } from "lucide-react";
import { RubinotCharacterValidator, type RubinotCharacterValidatorHandle } from "@/components/RubinotCharacterValidator";
import { useConfiguracoes } from "@/hooks/useConfiguracoes";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { motion } from "framer-motion";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import tibiaCoinImg from "@/assets/rubini_coins.gif";

const OUTFIT_IDS = [
  128, 129, 130, 131, 132, 133, 134, 137, 138, 139,
  140, 141, 142, 143, 144, 145, 146, 147, 148, 149,
];

const getOutfitUrl = (id: number) =>
  `https://outfit-images.ots.me/animatedOutfit.php?id=${id}&head=95&body=116&legs=121&feet=115&addons=0&mount=0`;

export const resolveAvatarUrl = (avatar_url: string | null): string | null => {
  if (!avatar_url) return null;
  if (avatar_url.startsWith("outfit_")) {
    const id = parseInt(avatar_url.replace("outfit_", ""), 10);
    if (!isNaN(id)) return getOutfitUrl(id);
    return null;
  }
  if (avatar_url.startsWith("http")) return avatar_url;
  return null;
};

interface Profile {
  telefone: string | null;
  email: string | null;
  nome: string | null;
  apelido: string | null;
  chave_pix: string | null;
  personagem_principal: string | null;
  avatar_url: string | null;
  preferencias_contato: string[] | null;
  discord_username: string | null;
  discord_link: string | null;
  revendedor: boolean | null;
}

interface Props {
  contato: string;
  token: string;
  canal: "whatsapp" | "email" | "senha";
  onProfileUpdate?: (profile: Partial<Profile>) => void;
  onSessionInvalid?: () => void;
}

const getFunctionErrorMessage = async (err: unknown, fallback: string) => {
  if (err && typeof err === "object") {
    const maybeErr = err as { message?: string; context?: Response };

    if (typeof maybeErr.message === "string") {
      const jsonStart = maybeErr.message.indexOf("{");
      if (jsonStart !== -1) {
        try {
          const parsed = JSON.parse(maybeErr.message.slice(jsonStart));
          if (typeof parsed?.error === "string") return parsed.error;
        } catch {
          // ignore
        }
      }
      if (maybeErr.message) return maybeErr.message;
    }

    if (maybeErr.context) {
      try {
        const parsed = await maybeErr.context.clone().json();
        if (typeof parsed?.error === "string") return parsed.error;
      } catch {
        // ignore
      }
    }
  }

  return fallback;
};

const ClientCadastroTab = ({ contato, token, canal, onProfileUpdate, onSessionInvalid }: Props) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasSenha, setHasSenha] = useState(false);
  const isPhoneLogin = canal === "whatsapp";
  const [profile, setProfile] = useState<Profile>({
    telefone: canal === "whatsapp" ? contato : null,
    email: canal === "email" ? contato : null,
    nome: null,
    apelido: null,
    chave_pix: null,
    personagem_principal: null,
    avatar_url: null,
    preferencias_contato: ["whatsapp"],
    discord_username: null,
    discord_link: null,
    revendedor: false,
  });

  // Password dialog state
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [pwStep, setPwStep] = useState<"choose_channel" | "verify" | "set_password">("choose_channel");
  const [pwCanal, setPwCanal] = useState<"whatsapp" | "email">(canal === "senha" ? "whatsapp" : canal as "whatsapp" | "email");
  const [pwToken, setPwToken] = useState("");
  const [pwTokenVerified, setPwTokenVerified] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwSending, setPwSending] = useState(false);
  const [pwVerifying, setPwVerifying] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);

  // Contact replacement state
  const [replaceField, setReplaceField] = useState<"telefone" | "email" | null>(null);
  const [replaceStep, setReplaceStep] = useState<"input" | "verify">("input");
  const [replaceValue, setReplaceValue] = useState("");
  const [replaceNormalized, setReplaceNormalized] = useState("");
  const [replaceCode, setReplaceCode] = useState("");
  const [replaceSending, setReplaceSending] = useState(false);
  const [replaceVerifying, setReplaceVerifying] = useState(false);

  // Delete account state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [delStep, setDelStep] = useState<"choose_channel" | "verify">("choose_channel");
  const [delCanal, setDelCanal] = useState<"whatsapp" | "email">(canal === "senha" ? "whatsapp" : canal as "whatsapp" | "email");
  const [delToken, setDelToken] = useState("");
  const [delSending, setDelSending] = useState(false);
  const [delVerifying, setDelVerifying] = useState(false);
  // Coins state
  const [coinsSaldo, setCoinsSaldo] = useState(0);
  const [coinsPendentes, setCoinsPendentes] = useState(0);
  const [coinsMovimentacoes, setCoinsMovimentacoes] = useState<{ id: string; tipo: string; valor: number; referencia: string; descricao: string | null; created_at: string }[]>([]);
  const [coinsLoading, setCoinsLoading] = useState(true);
  const [showExtrato, setShowExtrato] = useState(false);

  // Resgate state
  const [showResgate, setShowResgate] = useState(false);
  const [resgateChar, setResgateChar] = useState("");
  const [resgateServer, setResgateServer] = useState("");
  const [resgateQtd, setResgateQtd] = useState("");
  const [resgateValidating, setResgateValidating] = useState(false);
  const [resgateValidated, setResgateValidated] = useState(false);
  const [resgateLastValidated, setResgateLastValidated] = useState("");
  const [resgateSubmitting, setResgateSubmitting] = useState(false);

  const coinsRefLabels: Record<string, string> = {
    avaliacao_aprovada: "Avaliação aprovada",
    resgate: "Resgate de coins",
  };

  const loadCoins = async () => {
    setCoinsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("client-coins", {
        body: { contato, token },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setCoinsSaldo(data.saldo || 0);
      setCoinsPendentes(data.pendentes || 0);
      setCoinsMovimentacoes(data.movimentacoes || []);
    } catch {
      // silent
    }
    setCoinsLoading(false);
  };

  useEffect(() => {
    fetchProfile();
    loadCoins();
  }, []);

  useEffect(() => {
    onProfileUpdate?.({ revendedor: !!profile.revendedor } as any);
  }, [profile.revendedor]);

  // ===== Resgate =====
  const { data: configuracoes } = useConfiguracoes();
  const requireCharValidation = configuracoes?.somente_personagem_validado !== "false";
  const charValidatorRef = useRef<RubinotCharacterValidatorHandle>(null);
  const resgateCharInputRef = useRef<HTMLInputElement>(null);

  const handleResgateCharBlur = async () => {
    const trimmed = resgateChar.trim();
    if (!trimmed) return;
    if (resgateValidated && trimmed === resgateLastValidated) return;

    setResgateValidating(true);
    try {
      await charValidatorRef.current?.validate(trimmed);
    } finally {
      setResgateValidating(false);
    }
  };

  const handleCharConfirmed = (info: { name: string; world: string }) => {
    setResgateChar(info.name);
    setResgateLastValidated(info.name);
    setResgateValidated(true);
    if (info.world) setResgateServer(info.world);
  };

  const handleCharProceedUnvalidated = (name: string) => {
    setResgateLastValidated(name);
    setResgateValidated(true);
  };

  const handleCharCorrect = () => {
    setResgateValidated(false);
    setTimeout(() => resgateCharInputRef.current?.focus(), 100);
  };

  const handleResgateSubmit = async () => {
    const qtd = parseInt(resgateQtd, 10);
    if (!resgateChar.trim()) { toast.error("Informe o personagem"); return; }
    if (!resgateValidated) { toast.error("Valide o personagem antes de resgatar"); return; }
    if (!qtd || qtd <= 0) { toast.error("Informe a quantidade"); return; }
    if (qtd % 25 !== 0) { toast.error("A quantidade deve ser múltiplo de 25"); return; }
    if (qtd > coinsSaldo) { toast.error("Quantidade superior ao seu saldo"); return; }

    setResgateSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke("client-coins-resgate", {
        body: {
          contato, token,
          personagem_nome: resgateChar.trim(),
          servidor: resgateServer.trim() || null,
          quantidade: qtd,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success(`Resgate solicitado! ${qtd} TC serão entregues em breve.`);
      setShowResgate(false);
      setResgateChar(""); setResgateServer(""); setResgateQtd("");
      setResgateValidated(false); setResgateLastValidated("");
      loadCoins();
    } catch (err: unknown) {
      const message = await getFunctionErrorMessage(err, "Erro ao solicitar resgate");
      toast.error(message);
    } finally {
      setResgateSubmitting(false);
    }
  };

  const fetchProfile = async () => {
    try {
      const { data, error } = await supabase.functions.invoke("client-profile", {
        body: { contato, token },
      });
      if (error) throw error;
      if (data?.profile) {
        const p = data.profile;
        // If preferencias_contato is empty/null, set defaults based on available contacts
        if (!p.preferencias_contato || p.preferencias_contato.length === 0) {
          const defaults: string[] = ["plataforma"];
          if (p.telefone) defaults.push("whatsapp");
          if (p.email) defaults.push("email");
          p.preferencias_contato = defaults;
        }
        setProfile((prev) => ({ ...prev, ...p }));
      }
      setHasSenha(data?.has_senha || false);
    } catch (err: unknown) {
      const message = await getFunctionErrorMessage(err, "Erro ao carregar perfil");
      if (message === "Token inválido") {
        onSessionInvalid?.();
        return;
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Validate contact preferences against available data
    const prefs = profile.preferencias_contato || [];
    if (prefs.includes("whatsapp") && !profile.telefone) {
      toast.error("Você precisa ter um telefone cadastrado para selecionar WhatsApp como forma de contato");
      return;
    }
    if (prefs.includes("email") && !profile.email) {
      toast.error("Você precisa ter um e-mail cadastrado para selecionar E-mail como forma de contato");
      return;
    }
    if (prefs.includes("discord") && !profile.discord_username && !profile.discord_link) {
      toast.error("Você precisa cadastrar o usuário ou link do Discord para selecioná-lo como forma de contato");
      return;
    }
    if (prefs.length === 0) {
      toast.error("Selecione ao menos uma forma de contato");
      return;
    }

    setSaving(true);
    try {
      const { data, error } = await supabase.functions.invoke("client-profile", {
        body: { contato, token, profile },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success("Cadastro salvo com sucesso!");
      if (data?.profile) {
        setProfile((prev) => ({ ...prev, ...data.profile }));
        onProfileUpdate?.({
          apelido: data.profile.apelido,
          avatar_url: data.profile.avatar_url,
          nome: data.profile.nome,
        });
      }
      setHasSenha(data?.has_senha || false);
    } catch (err: unknown) {
      const message = await getFunctionErrorMessage(err, "Erro ao salvar");
      if (message === "Token inválido") {
        onSessionInvalid?.();
        return;
      }
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (file: File) => {
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Imagem deve ter no máximo 2MB");
      return;
    }
    const ext = file.name.split(".").pop();
    const path = `avatars/${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from("comprovantes").upload(path, file);
    if (error) {
      toast.error("Erro ao enviar imagem");
      return;
    }
    const { data: urlData } = supabase.storage.from("comprovantes").getPublicUrl(path);
    setProfile((prev) => ({ ...prev, avatar_url: urlData.publicUrl }));
    toast.success("Imagem enviada!");
  };

  // Password flow
  const handleSendPwToken = async () => {
    const dest = pwCanal === "whatsapp" ? profile.telefone : profile.email;
    if (!dest) {
      toast.error(`Preencha seu ${pwCanal === "whatsapp" ? "telefone" : "e-mail"} antes`);
      return;
    }
    setPwSending(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-verification", {
        body: { contato: dest, canal: pwCanal },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success("Código enviado!");
      setPwStep("verify");
    } catch (err: any) {
      toast.error(err.message || "Erro ao enviar código");
    }
    setPwSending(false);
  };

  const handleVerifyPwToken = async () => {
    const dest = pwCanal === "whatsapp" ? profile.telefone : profile.email;
    setPwVerifying(true);
    try {
      const { data, error } = await supabase.functions.invoke("verify-token", {
        body: { contato: dest, token: pwToken },
      });
      if (error) throw error;
      if (!data?.valid) {
        toast.error(data?.error || "Código inválido");
        setPwVerifying(false);
        return;
      }
      setPwTokenVerified(true);
      setPwStep("set_password");
    } catch (err: any) {
      toast.error(err.message || "Erro ao verificar");
    }
    setPwVerifying(false);
  };

  const handleSetPassword = async () => {
    if (newPassword.length < 6) {
      toast.error("Senha deve ter pelo menos 6 caracteres");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("As senhas não coincidem");
      return;
    }
    const dest = pwCanal === "whatsapp" ? profile.telefone : profile.email;
    setPwSaving(true);
    try {
      const { data, error } = await supabase.functions.invoke("client-set-password", {
        body: { contato: dest, token: pwToken, senha: newPassword },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success("Senha definida com sucesso!");
      setHasSenha(true);
      closePasswordDialog();
    } catch (err: any) {
      toast.error(err.message || "Erro ao definir senha");
    }
    setPwSaving(false);
  };

  const closePasswordDialog = () => {
    setShowPasswordDialog(false);
    setPwStep("choose_channel");
    setPwToken("");
    setPwTokenVerified(false);
    setNewPassword("");
    setConfirmPassword("");
  };

  const openPasswordDialog = () => {
    setShowPasswordDialog(true);
  };

  const openReplaceDialog = (field: "telefone" | "email") => {
    setReplaceField(field);
    setReplaceStep("input");
    setReplaceValue("");
    setReplaceNormalized("");
    setReplaceCode("");
  };

  const closeReplaceDialog = () => {
    setReplaceField(null);
    setReplaceStep("input");
    setReplaceValue("");
    setReplaceNormalized("");
    setReplaceCode("");
  };

  // Delete account flow
  const closeDeleteDialog = () => {
    setShowDeleteDialog(false);
    setDelStep("choose_channel");
    setDelToken("");
  };

  const handleSendDelToken = async () => {
    const dest = delCanal === "whatsapp" ? profile.telefone : profile.email;
    if (!dest) {
      toast.error(`Preencha seu ${delCanal === "whatsapp" ? "telefone" : "e-mail"} antes`);
      return;
    }
    setDelSending(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-verification", {
        body: { contato: dest, canal: delCanal },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success("Código enviado!");
      setDelStep("verify");
    } catch (err: any) {
      toast.error(err.message || "Erro ao enviar código");
    }
    setDelSending(false);
  };

  const handleVerifyDelToken = async () => {
    const dest = delCanal === "whatsapp" ? profile.telefone : profile.email;
    setDelVerifying(true);
    try {
      // First verify the OTP token
      const { data: verifyData, error: verifyErr } = await supabase.functions.invoke("verify-token", {
        body: { contato: dest, token: delToken },
      });
      if (verifyErr) throw verifyErr;
      if (!verifyData?.valid) {
        toast.error(verifyData?.error || "Código inválido");
        setDelVerifying(false);
        return;
      }

      // Now delete the account using the session token
      const { data, error } = await supabase.functions.invoke("delete-account", {
        body: { contato, token },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast.success("Conta excluída com sucesso. Seus dados foram removidos.");
      closeDeleteDialog();
      // Logout
      onSessionInvalid?.();
    } catch (err: any) {
      toast.error(err.message || "Erro ao excluir conta");
    }
    setDelVerifying(false);
  };

  const handleReplaceCheck = async () => {
    if (!replaceValue.trim() || !replaceField) return;
    setReplaceSending(true);
    try {
      const { data, error } = await supabase.functions.invoke("client-replace-contact", {
        body: { contato, token, action: "check", campo: replaceField, novo_valor: replaceValue.trim() },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setReplaceNormalized(data.normalized_value);
      setReplaceStep("verify");
      toast.success(data.message || "Código enviado!");
    } catch (err: unknown) {
      const message = await getFunctionErrorMessage(err, "Erro ao verificar");
      toast.error(message);
    }
    setReplaceSending(false);
  };

  const handleReplaceConfirm = async () => {
    if (replaceCode.length !== 5 || !replaceField) return;
    setReplaceVerifying(true);
    try {
      const { data, error } = await supabase.functions.invoke("client-replace-contact", {
        body: { contato, token, action: "confirm", campo: replaceField, novo_valor: replaceNormalized, codigo: replaceCode },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setProfile((p) => ({ ...p, [replaceField!]: data[replaceField!] }));
      toast.success(`${replaceField === "telefone" ? "Telefone" : "E-mail"} atualizado com sucesso!`);
      closeReplaceDialog();
      // Refresh profile
      fetchProfile();
    } catch (err: unknown) {
      const message = await getFunctionErrorMessage(err, "Erro ao confirmar");
      toast.error(message);
    }
    setReplaceVerifying(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Avatar + Coins Balance */}
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-2">
          <label className="font-pixel text-[8px] text-muted-foreground">IMAGEM DE PERFIL</label>
          <label className="flex items-center gap-3 cursor-pointer group">
            <div className="relative w-12 h-12 rounded-sm border-2 border-dashed border-border/50 group-hover:border-primary/50 flex items-center justify-center transition-all overflow-hidden">
              {profile.avatar_url && resolveAvatarUrl(profile.avatar_url) ? (
                <img src={resolveAvatarUrl(profile.avatar_url)!} alt="Avatar" className="w-full h-full object-cover" />
              ) : (
                <Upload className="w-4 h-4 text-muted-foreground" />
              )}
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                <Upload className="w-4 h-4 text-white" />
              </div>
            </div>
            <span className="text-[10px] text-muted-foreground group-hover:text-primary transition-colors">
              {profile.avatar_url ? "Trocar imagem" : "Enviar imagem"}
            </span>
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && handleAvatarUpload(e.target.files[0])}
            />
          </label>
        </div>

        {/* Coins Balance */}
        <div className="flex flex-col items-end gap-1 pt-4">
          <div className="flex items-center gap-2">
            <img src={tibiaCoinImg} alt="Coin" className="w-5 h-5" />
            <span className="font-pixel text-lg text-primary gold-text-glow">
              {coinsLoading ? "..." : coinsSaldo}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowExtrato(true)}
              className="text-[9px] text-primary hover:text-primary/80 underline transition-colors font-pixel"
            >
              ver extrato
            </button>
            {coinsSaldo >= 25 ? (
              <button
                onClick={() => { setResgateChar(profile.personagem_principal || ""); setResgateValidated(false); setResgateLastValidated(""); setResgateQtd(String(coinsSaldo - (coinsSaldo % 25))); setShowResgate(true); }}
                className="text-[9px] text-accent hover:text-accent/80 underline transition-colors font-pixel flex items-center gap-1"
              >
                <Gift className="w-3 h-3" />
                resgatar
              </button>
            ) : (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-[9px] text-muted-foreground/60 underline cursor-not-allowed font-pixel flex items-center gap-1">
                      <Lock className="w-3 h-3" />
                      resgatar
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-[220px]">
                    <p className="text-[10px]">
                      O resgate fica disponível a partir de <strong>25 RC</strong> de saldo.
                      Avalie negociações para acumular mais coins.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>
      </div>

      {/* Fields */}
      <div className="space-y-3">
        <div>
          <label className="font-pixel text-[8px] text-muted-foreground">APELIDO</label>
          <input
            type="text"
            className="tibia-input rounded-sm text-sm"
            value={profile.apelido || ""}
            onChange={(e) => setProfile((p) => ({ ...p, apelido: e.target.value }))}
            placeholder="Seu apelido único"
            maxLength={50}
            onPaste={(e) => { const pasted = e.clipboardData.getData("text"); if (((profile.apelido || "") + pasted).length > 50) { e.preventDefault(); setProfile((p) => ({ ...p, apelido: ((p.apelido || "") + pasted).slice(0, 50) })); } }}
          />
        </div>
        <div>
          <label className="font-pixel text-[8px] text-muted-foreground">TELEFONE</label>
          <div className="flex gap-2">
            <input
              type="text"
              className="tibia-input rounded-sm text-sm flex-1 opacity-70"
              value={profile.telefone || ""}
              readOnly
              placeholder="Não cadastrado"
            />
            <button
              onClick={() => openReplaceDialog("telefone")}
              className="tibia-tab rounded-sm px-3 hover:text-primary flex items-center gap-1 shrink-0"
            >
              <RefreshCw className="w-3 h-3" />
              <span className="font-pixel text-[7px]">Substituir</span>
            </button>
          </div>
        </div>
        <div>
          <label className="font-pixel text-[8px] text-muted-foreground">E-MAIL</label>
          <div className="flex gap-2">
            <input
              type="email"
              className="tibia-input rounded-sm text-sm flex-1 opacity-70"
              value={profile.email || ""}
              readOnly
              placeholder="Não cadastrado"
            />
            <button
              onClick={() => openReplaceDialog("email")}
              className="tibia-tab rounded-sm px-3 hover:text-primary flex items-center gap-1 shrink-0"
            >
              <RefreshCw className="w-3 h-3" />
              <span className="font-pixel text-[7px]">Substituir</span>
            </button>
          </div>
        </div>
        <div>
          <label className="font-pixel text-[8px] text-muted-foreground">NOME</label>
          <input
            type="text"
            className="tibia-input rounded-sm text-sm"
            value={profile.nome || ""}
            onChange={(e) => setProfile((p) => ({ ...p, nome: e.target.value }))}
            placeholder="Seu nome"
            maxLength={50}
            onPaste={(e) => { const pasted = e.clipboardData.getData("text"); if (((profile.nome || "") + pasted).length > 50) { e.preventDefault(); setProfile((p) => ({ ...p, nome: ((p.nome || "") + pasted).slice(0, 50) })); } }}
          />
        </div>
        <div>
          <label className="font-pixel text-[8px] text-muted-foreground">CHAVE PIX</label>
          <input
            type="text"
            className="tibia-input rounded-sm text-sm"
            value={profile.chave_pix || ""}
            onChange={(e) => setProfile((p) => ({ ...p, chave_pix: e.target.value }))}
            placeholder="CPF, e-mail, telefone ou chave aleatória"
            maxLength={100}
          />
        </div>
        <div>
          <label className="font-pixel text-[8px] text-muted-foreground">PERSONAGEM PRINCIPAL</label>
          <input
            type="text"
            className="tibia-input rounded-sm text-sm"
            value={profile.personagem_principal || ""}
            onChange={(e) => setProfile((p) => ({ ...p, personagem_principal: e.target.value }))}
            placeholder="Nome do personagem"
            maxLength={50}
          />
        </div>

        <div>
          <label className="font-pixel text-[8px] text-muted-foreground">USUÁRIO DISCORD <span className="opacity-60">(opcional)</span></label>
          <input
            type="text"
            className="tibia-input rounded-sm text-sm"
            value={profile.discord_username || ""}
            onChange={(e) => setProfile((p) => ({ ...p, discord_username: e.target.value.replace(/\s/g, "").slice(0, 50) }))}
            placeholder="ex: usuario ou usuario#1234"
            maxLength={50}
            autoComplete="off"
          />
        </div>
        <div>
          <label className="font-pixel text-[8px] text-muted-foreground">LINK DISCORD <span className="opacity-60">(opcional, servidor/perfil)</span></label>
          <input
            type="url"
            className="tibia-input rounded-sm text-sm"
            value={profile.discord_link || ""}
            onChange={(e) => setProfile((p) => ({ ...p, discord_link: e.target.value.trim().slice(0, 200) }))}
            placeholder="https://discord.gg/..."
            maxLength={200}
            autoComplete="off"
          />
        </div>

        {/* Contact Preferences */}
        <div>
          <label className="font-pixel text-[8px] text-muted-foreground">FORMAS DE CONTATO</label>
          <p className="text-[7px] text-muted-foreground mb-1">Como deseja receber contato de outros usuários?</p>
          <div className="flex flex-wrap gap-3">
            {[
              { key: "whatsapp", label: "WhatsApp", icon: "💬" },
              { key: "email", label: "E-mail", icon: "✉️" },
              { key: "discord", label: "Discord", icon: "🎮" },
              { key: "plataforma", label: "Plataforma", icon: "💻" },
            ].map((opt) => (
              <label key={opt.key} className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={(profile.preferencias_contato || ["whatsapp"]).includes(opt.key)}
                  onChange={(e) => {
                    // Validate: can't enable without corresponding data
                    if (e.target.checked) {
                      if (opt.key === "whatsapp" && !profile.telefone) {
                        toast.error("Cadastre um telefone antes de selecionar WhatsApp");
                        return;
                      }
                      if (opt.key === "email" && !profile.email) {
                        toast.error("Cadastre um e-mail antes de selecionar E-mail");
                        return;
                      }
                      if (opt.key === "discord" && !profile.discord_username && !profile.discord_link) {
                        toast.error("Cadastre o usuário ou link do Discord antes de selecioná-lo");
                        return;
                      }
                    }
                    const current = profile.preferencias_contato || ["whatsapp"];
                    let updated: string[];
                    if (e.target.checked) {
                      updated = [...current, opt.key];
                    } else {
                      updated = current.filter((v) => v !== opt.key);
                    }
                    // Ensure at least one is selected
                    if (updated.length === 0) {
                      toast.error("Selecione ao menos uma forma de contato");
                      return;
                    }
                    setProfile((p) => ({ ...p, preferencias_contato: updated }));
                  }}
                  className="rounded border-border text-primary focus:ring-primary"
                />
                <span className="text-[10px]">{opt.icon} {opt.label}</span>
              </label>
            ))}
          </div>
        </div>
        <label
          className="relative flex items-center gap-3 p-3 rounded-sm border-2 border-primary/60 cursor-pointer overflow-hidden group bg-gradient-to-r from-primary/10 via-amber-500/10 to-primary/10 hover:from-primary/20 hover:via-amber-500/20 hover:to-primary/20 transition-all"
          style={{ boxShadow: "0 0 12px hsl(var(--primary) / 0.45)" }}
        >
          <span className="pointer-events-none absolute inset-0 rounded-sm animate-pulse" style={{ boxShadow: "inset 0 0 18px hsl(var(--primary) / 0.35)" }} />
          <input
            type="checkbox"
            className="relative z-10 w-4 h-4 accent-primary cursor-pointer"
            checked={!!profile.revendedor}
            onChange={(e) => setProfile((p) => ({ ...p, revendedor: e.target.checked }))}
          />
          <div className="relative z-10 flex flex-col">
            <span className="font-pixel text-[9px] text-primary uppercase tracking-wide flex items-center gap-1">
              <Star className="w-3 h-3 fill-primary" /> Sou revendedor
            </span>
            <span className="text-[10px] text-muted-foreground">Marque se você compra e revende itens na plataforma</span>
          </div>
        </label>
      </div>

      {/* Save */}
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={handleSave}
        disabled={saving}
        className="tibia-btn w-full rounded-sm flex items-center justify-center gap-2 disabled:opacity-40"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (
          <span className="font-pixel text-[8px]">Salvar Cadastro</span>
        )}
      </motion.button>

      {/* Password button */}
      <button
        onClick={openPasswordDialog}
        className="tibia-tab w-full rounded-sm hover:text-primary flex items-center justify-center gap-2"
      >
        <Lock className="w-3 h-3" />
        <span className="font-pixel text-[8px]">{hasSenha ? "Redefinir Senha" : "Definir Senha"}</span>
      </button>

      {/* Danger Zone - Delete Account */}
      <div className="mt-6 rounded-sm border border-destructive/40 bg-destructive/10 p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Badge variant="destructive" className="font-pixel text-[7px] uppercase">
            <AlertTriangle className="w-3 h-3 mr-1" />
            Cuidado
          </Badge>
        </div>
        <p className="text-[10px] text-muted-foreground">
          Ao excluir sua conta, todos os seus dados serão permanentemente removidos da plataforma em cumprimento à LGPD. Esta ação não pode ser desfeita.
        </p>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setShowDeleteDialog(true)}
          className="w-full rounded-sm border border-destructive/60 bg-destructive/20 hover:bg-destructive/30 text-destructive py-2 flex items-center justify-center gap-2 transition-colors"
        >
          <Trash2 className="w-3.5 h-3.5" />
          <span className="font-pixel text-[8px]">Excluir conta e apagar dados</span>
        </motion.button>
      </div>

      {/* Delete Account Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={closeDeleteDialog}>
          <div className="tibia-border bg-card rounded-sm p-4 w-full max-w-sm space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-pixel text-[9px] text-destructive text-center">EXCLUIR CONTA</h3>
            <p className="text-xs text-muted-foreground text-center">
              Para confirmar a exclusão, enviaremos um código de verificação.
            </p>

            {delStep === "choose_channel" && (
              <div className="space-y-3">
                {profile.telefone && profile.email ? (
                  <div className="flex gap-2">
                    <button
                      onClick={() => setDelCanal("whatsapp")}
                      className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-sm transition-all ${
                        delCanal === "whatsapp" ? "tibia-border bg-secondary text-primary" : "tibia-border-inner bg-card text-muted-foreground"
                      }`}
                    >
                      <MessageCircle className="w-4 h-4" />
                      <span className="font-pixel text-[7px]">WhatsApp</span>
                    </button>
                    <button
                      onClick={() => setDelCanal("email")}
                      className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-sm transition-all ${
                        delCanal === "email" ? "tibia-border bg-secondary text-primary" : "tibia-border-inner bg-card text-muted-foreground"
                      }`}
                    >
                      <Mail className="w-4 h-4" />
                      <span className="font-pixel text-[7px]">E-mail</span>
                    </button>
                  </div>
                ) : (
                  <p className="text-xs text-center text-foreground">
                    Enviar para: <span className="text-primary">{profile.telefone || profile.email}</span>
                  </p>
                )}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleSendDelToken}
                  disabled={delSending}
                  className="w-full rounded-sm border border-destructive/60 bg-destructive/20 hover:bg-destructive/30 text-destructive py-2 flex items-center justify-center gap-2 transition-colors disabled:opacity-40"
                >
                  {delSending ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                    <span className="font-pixel text-[8px]">Enviar Código</span>
                  )}
                </motion.button>
              </div>
            )}

            {delStep === "verify" && (
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground text-center">Digite o código de 5 dígitos</p>
                <input
                  type="text"
                  maxLength={5}
                  className="tibia-input rounded-sm text-center text-2xl tracking-[0.5em]"
                  placeholder="00000"
                  value={delToken}
                  onChange={(e) => setDelToken(e.target.value.replace(/\D/g, "").slice(0, 5))}
                />
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleVerifyDelToken}
                  disabled={delToken.length !== 5 || delVerifying}
                  className="w-full rounded-sm border border-destructive/60 bg-destructive/20 hover:bg-destructive/30 text-destructive py-2 flex items-center justify-center gap-2 transition-colors disabled:opacity-40"
                >
                  {delVerifying ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                    <span className="font-pixel text-[8px]">Confirmar Exclusão</span>
                  )}
                </motion.button>
              </div>
            )}

            <button
              onClick={closeDeleteDialog}
              className="tibia-tab w-full rounded-sm hover:text-primary text-xs"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {showPasswordDialog && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={closePasswordDialog}>
          <div className="tibia-border bg-card rounded-sm p-4 w-full max-w-sm space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-pixel text-[9px] text-primary text-center">
              {hasSenha ? "REDEFINIR SENHA" : "DEFINIR SENHA"}
            </h3>

            {pwStep === "choose_channel" && (
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground text-center">
                  Enviaremos um código de verificação para confirmar sua identidade.
                </p>
                {profile.telefone && profile.email ? (
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPwCanal("whatsapp")}
                      className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-sm transition-all ${
                        pwCanal === "whatsapp" ? "tibia-border bg-secondary text-primary" : "tibia-border-inner bg-card text-muted-foreground"
                      }`}
                    >
                      <MessageCircle className="w-4 h-4" />
                      <span className="font-pixel text-[7px]">WhatsApp</span>
                    </button>
                    <button
                      onClick={() => setPwCanal("email")}
                      className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-sm transition-all ${
                        pwCanal === "email" ? "tibia-border bg-secondary text-primary" : "tibia-border-inner bg-card text-muted-foreground"
                      }`}
                    >
                      <Mail className="w-4 h-4" />
                      <span className="font-pixel text-[7px]">E-mail</span>
                    </button>
                  </div>
                ) : (
                  <p className="text-xs text-center text-foreground">
                    Enviar para: <span className="text-primary">{profile.telefone || profile.email}</span>
                  </p>
                )}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleSendPwToken}
                  disabled={pwSending}
                  className="tibia-btn w-full rounded-sm flex items-center justify-center gap-2 disabled:opacity-40"
                >
                  {pwSending ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                    <span className="font-pixel text-[8px]">Enviar Código</span>
                  )}
                </motion.button>
              </div>
            )}

            {pwStep === "verify" && (
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground text-center">Digite o código de 5 dígitos</p>
                <input
                  type="text"
                  maxLength={5}
                  className="tibia-input rounded-sm text-center text-2xl tracking-[0.5em]"
                  placeholder="00000"
                  value={pwToken}
                  onChange={(e) => setPwToken(e.target.value.replace(/\D/g, "").slice(0, 5))}
                />
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleVerifyPwToken}
                  disabled={pwToken.length !== 5 || pwVerifying}
                  className="tibia-btn w-full rounded-sm flex items-center justify-center gap-2 disabled:opacity-40"
                >
                  {pwVerifying ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                    <span className="font-pixel text-[8px]">Verificar</span>
                  )}
                </motion.button>
              </div>
            )}

            {pwStep === "set_password" && (
              <div className="space-y-3">
                <div>
                  <label className="font-pixel text-[8px] text-muted-foreground">NOVA SENHA</label>
                  <input
                    type="password"
                    className="tibia-input rounded-sm text-sm"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Mínimo 6 caracteres"
                    maxLength={100}
                  />
                </div>
                <div>
                  <label className="font-pixel text-[8px] text-muted-foreground">CONFIRMAR SENHA</label>
                  <input
                    type="password"
                    className="tibia-input rounded-sm text-sm"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Repita a senha"
                    maxLength={100}
                  />
                </div>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleSetPassword}
                  disabled={pwSaving}
                  className="tibia-btn w-full rounded-sm flex items-center justify-center gap-2 disabled:opacity-40"
                >
                  {pwSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                    <span className="font-pixel text-[8px]">Salvar Senha</span>
                  )}
                </motion.button>
              </div>
            )}

            <button
              onClick={closePasswordDialog}
              className="tibia-tab w-full rounded-sm hover:text-primary text-xs"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Replace Contact Dialog */}
      {replaceField && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={closeReplaceDialog}>
          <div className="tibia-border bg-card rounded-sm p-4 w-full max-w-sm space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-pixel text-[9px] text-primary text-center">
              SUBSTITUIR {replaceField === "telefone" ? "TELEFONE" : "E-MAIL"}
            </h3>

            {replaceStep === "input" && (
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground text-center">
                  Informe o novo {replaceField === "telefone" ? "número de telefone" : "endereço de e-mail"}. Um código de verificação será enviado.
                </p>
                <input
                  type="text"
                  className="tibia-input rounded-sm text-sm"
                  placeholder={replaceField === "telefone" ? "5511999999999" : "novo@email.com"}
                  value={replaceValue}
                  maxLength={replaceField === "telefone" ? 15 : 100}
                  onChange={(e) => setReplaceValue(e.target.value)}
                  onPaste={(e) => { const max = replaceField === "telefone" ? 15 : 100; const pasted = e.clipboardData.getData("text"); if ((replaceValue + pasted).length > max) { e.preventDefault(); setReplaceValue((replaceValue + pasted).slice(0, max)); } }}
                />
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleReplaceCheck}
                  disabled={!replaceValue.trim() || replaceSending}
                  className="tibia-btn w-full rounded-sm flex items-center justify-center gap-2 disabled:opacity-40"
                >
                  {replaceSending ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                    <>
                      <span className="font-pixel text-[8px]">Enviar Código</span>
                      <ArrowRight className="w-3 h-3" />
                    </>
                  )}
                </motion.button>
              </div>
            )}

            {replaceStep === "verify" && (
              <div className="space-y-3">
                <div className="text-center">
                  <CheckCircle className="w-8 h-8 text-primary mx-auto mb-2" />
                  <p className="text-xs text-muted-foreground">
                    Código enviado para <span className="text-primary">{replaceNormalized}</span>
                  </p>
                </div>
                <input
                  type="text"
                  maxLength={5}
                  className="tibia-input rounded-sm text-center text-2xl tracking-[0.5em]"
                  placeholder="00000"
                  value={replaceCode}
                  onChange={(e) => setReplaceCode(e.target.value.replace(/\D/g, "").slice(0, 5))}
                />
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleReplaceConfirm}
                  disabled={replaceCode.length !== 5 || replaceVerifying}
                  className="tibia-btn w-full rounded-sm flex items-center justify-center gap-2 disabled:opacity-40"
                >
                  {replaceVerifying ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                    <span className="font-pixel text-[8px]">Confirmar</span>
                  )}
                </motion.button>
              </div>
            )}

            <button
              onClick={closeReplaceDialog}
              className="tibia-tab w-full rounded-sm hover:text-primary text-xs"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Resgate Dialog */}
      <Dialog open={showResgate} onOpenChange={(o) => !resgateSubmitting && setShowResgate(o)}>
        <DialogContent className="tibia-panel border-2 border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-pixel text-sm text-primary flex items-center gap-2">
              <Gift className="w-4 h-4" />
              Resgatar Coins
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="tibia-border-inner rounded-sm p-3 bg-secondary/30 flex items-center justify-between">
              <span className="font-pixel text-[8px] text-muted-foreground">SALDO DISPONÍVEL</span>
              <div className="flex items-center gap-2">
                <img src={tibiaCoinImg} alt="Coin" className="w-4 h-4" />
                <span className="font-pixel text-sm text-primary gold-text-glow">{coinsSaldo}</span>
              </div>
            </div>

            <div className="space-y-1">
              <label className="font-pixel text-[8px] text-muted-foreground uppercase tracking-wider">
                Personagem que receberá:
              </label>
              <div className="relative">
                <input
                  ref={resgateCharInputRef}
                  type="text"
                  className={`tibia-input rounded-sm w-full ${resgateValidated ? "pr-8" : ""}`}
                  placeholder="Nome do personagem"
                  value={resgateChar}
                  maxLength={50}
                  disabled={resgateValidating || resgateSubmitting}
                  onChange={(e) => { setResgateChar(e.target.value); setResgateValidated(false); }}
                  onBlur={handleResgateCharBlur}
                />
                {resgateValidating && (
                  <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-primary animate-spin" />
                )}
                {resgateValidated && !resgateValidating && (
                  <Shield className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500 fill-green-500/20" />
                )}
              </div>
              {profile.personagem_principal && profile.personagem_principal !== resgateChar && (
                <div className="flex justify-end mt-0.5">
                  <button
                    type="button"
                    className="font-pixel text-[8px] text-primary hover:text-primary/80 underline underline-offset-2 transition-colors cursor-pointer"
                    onClick={() => {
                      setResgateChar(profile.personagem_principal!);
                      setResgateValidated(false);
                      setTimeout(() => handleResgateCharBlur(), 50);
                    }}
                  >
                    usar {profile.personagem_principal}
                  </button>
                </div>
              )}
            </div>

            <div className="space-y-1">
              <label className="font-pixel text-[8px] text-muted-foreground uppercase tracking-wider">
                Quantidade (múltiplo de 25)
              </label>
              <input
                type="number"
                className="tibia-input rounded-sm w-full"
                placeholder="Ex: 25, 50, 75..."
                value={resgateQtd}
                step={25}
                min={25}
                max={coinsSaldo}
                disabled={resgateSubmitting}
                onChange={(e) => setResgateQtd(e.target.value.replace(/[^0-9]/g, ""))}
              />
              <div className="flex flex-wrap gap-1 pt-1">
                {[25, 50, 100, 250, 500].filter((v) => v <= coinsSaldo).map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setResgateQtd(String(v))}
                    className="tibia-tab rounded-sm px-2 py-1 font-pixel text-[7px] hover:text-primary"
                    disabled={resgateSubmitting}
                  >
                    {v}
                  </button>
                ))}
                {coinsSaldo - (coinsSaldo % 25) >= 25 && (
                  <button
                    type="button"
                    onClick={() => setResgateQtd(String(coinsSaldo - (coinsSaldo % 25)))}
                    className="tibia-tab rounded-sm px-2 py-1 font-pixel text-[7px] hover:text-primary"
                    disabled={resgateSubmitting}
                  >
                    máx ({coinsSaldo - (coinsSaldo % 25)})
                  </button>
                )}
              </div>
            </div>

            <p className="text-[10px] text-muted-foreground">
              Após confirmar, o saldo será debitado e os coins serão entregues no personagem informado em breve.
            </p>

            <div className="flex gap-2 pt-2">
              <button
                onClick={() => setShowResgate(false)}
                disabled={resgateSubmitting}
                className="tibia-tab rounded-sm flex-1 py-2 font-pixel text-[8px]"
              >
                Cancelar
              </button>
              <button
                onClick={handleResgateSubmit}
                disabled={resgateSubmitting || !resgateValidated || !resgateQtd}
                className="tibia-btn rounded-sm flex-1 py-2 font-pixel text-[8px] gold-glow flex items-center justify-center gap-1 disabled:opacity-50"
              >
                {resgateSubmitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Gift className="w-3 h-3" />}
                Confirmar Resgate
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <RubinotCharacterValidator
        ref={charValidatorRef}
        requireValidation={requireCharValidation}
        onConfirm={handleCharConfirmed}
        onProceedUnvalidated={handleCharProceedUnvalidated}
        onCorrect={handleCharCorrect}
      />

      {/* Extrato Dialog */}
      <Dialog open={showExtrato} onOpenChange={setShowExtrato}>
        <DialogContent className="tibia-panel border-2 border-border max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-pixel text-sm text-primary flex items-center gap-2">
              <ArrowDownCircle className="w-4 h-4" />
              Extrato de Coins
            </DialogTitle>
          </DialogHeader>

          {/* Balance summary */}
          <div className="tibia-border rounded-sm p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <img src={tibiaCoinImg} alt="Coin" className="w-6 h-6" />
                <span className="font-pixel text-[9px] text-muted-foreground">SEU SALDO</span>
              </div>
              <span className="font-pixel text-lg text-primary gold-text-glow">{coinsSaldo}</span>
            </div>
            {coinsPendentes > 0 && (
              <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                <Star className="w-3 h-3 text-accent" />
                <span>{coinsPendentes} avaliação(ões) aguardando aprovação</span>
              </div>
            )}
          </div>

          {/* Movimentações */}
          {coinsMovimentacoes.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-8">
              Nenhuma movimentação ainda.
            </p>
          ) : (
            <div className="space-y-2">
              {coinsMovimentacoes.map((m) => (
                <div key={m.id} className="tibia-border-inner bg-secondary/30 rounded-sm p-3 flex items-center gap-3">
                  <div className="flex-shrink-0">
                    {m.tipo === "credito" ? (
                      <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                        <Coins className="w-4 h-4 text-green-400" />
                      </div>
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-destructive/20 flex items-center justify-center">
                        <Coins className="w-4 h-4 text-destructive" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[11px] text-foreground font-medium truncate">
                        {coinsRefLabels[m.referencia] || m.referencia}
                      </span>
                      <Badge
                        variant="outline"
                        className={`font-pixel text-[8px] shrink-0 ${
                          m.tipo === "credito"
                            ? "bg-green-500/20 text-green-400 border-green-500/40"
                            : "bg-destructive/20 text-destructive border-destructive/40"
                        }`}
                      >
                        {m.tipo === "credito" ? "+" : "-"}{m.valor}
                      </Badge>
                    </div>
                    {m.descricao && (
                      <p className="text-[9px] text-muted-foreground truncate">{m.descricao}</p>
                    )}
                    <p className="text-[8px] text-muted-foreground mt-0.5">
                      {new Date(m.created_at).toLocaleString("pt-BR")}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientCadastroTab;
